import { API_ENDPOINTS } from '../config/api.config';
import { isFutureDay } from './dateHelpers';

export function accumulateMonthlySummaryCell(existing, item) {
  const actual = Number(item.actual_effort ?? item.total_effort) || 0;
  const forecast = Number(item.forecast_effort) || 0;
  if (!existing) {
    return { ...item, actual_effort: actual, forecast_effort: forecast, total_effort: actual };
  }
  const nextActual = (Number(existing.actual_effort) || 0) + actual;
  return {
    ...existing,
    actual_effort: nextActual,
    forecast_effort: (Number(existing.forecast_effort) || 0) + forecast,
    total_effort: nextActual,
  };
}

export function pivotMinutesForDay(obj, filterYear, filterMonth, day) {
  if (!obj) return 0;
  const dateStr = `${filterYear}-${String(filterMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  if (isFutureDay(dateStr)) return Number(obj.forecast_effort) || 0;
  return Number(obj.actual_effort ?? obj.total_effort) || 0;
}

function resolveDisplayName(user) {
  if (!user) return '';
  return (
    user.username ||
    user.name ||
    (user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : '') ||
    user.email ||
    ''
  );
}

/**
 * Monthly worklog summary for multiple users (single API call).
 * @param {number[]} userIds
 * @param {string} yearMonth - "YYYY-MM"
 * @param {number[]} [activityIds]
 */
export async function fetchMonthlyWorklogSummaryForUsers(userIds, yearMonth, activityIds = []) {
  if (!userIds?.length) return [];
  const params = new URLSearchParams({ year_month: yearMonth, include_forecast: 'true' });
  params.set('user_ids', userIds.map(String).join(','));
  if (activityIds?.length) {
    params.set('activity_ids', activityIds.map(String).join(','));
  }
  const res = await fetch(`${API_ENDPOINTS.MONTHLY_WORKLOG_SUMMARY_USER_PROJECT}?${params}`);
  if (!res.ok) {
    throw new Error(`Failed to load monthly worklog summary (${res.status})`);
  }
  const json = await res.json();
  return json.data || [];
}

function mapHierarchyUserRow(user, rootPmName) {
  const userName = resolveDisplayName(user);
  const pmId = user.people_manager?.id ?? user.people_manager_id ?? user.id_people_manager;
  const pmName = user.people_manager?.username ?? user.people_manager_name ?? rootPmName;
  return {
    id: user.id,
    name: userName,
    username: userName,
    email: user.email,
    isPeopleManager: !!user.is_people_manager,
    peopleManagerName: pmName,
    peopleManagerId: pmId,
  };
}

/**
 * Gerarchia PM via chiamate BFS sui report diretti (tollerante ai cicli in id_people_manager).
 */
export async function fetchManagedUsersHierarchyBfs(rootPmId, rootPmName = 'Direct reports') {
  const usersById = new Map();
  const pmQueue = [rootPmId];
  const visitedPms = new Set();

  while (pmQueue.length > 0) {
    const pmId = pmQueue.shift();
    if (visitedPms.has(pmId)) continue;
    visitedPms.add(pmId);

    const res = await fetch(`${API_ENDPOINTS.USERS}?id_people_manager=${pmId}`);
    if (!res.ok) {
      throw new Error(`Failed to load direct reports for people manager ${pmId} (${res.status})`);
    }
    const data = await res.json();
    for (const user of data.users || []) {
      const pmIdFromUser = user.people_manager?.id ?? user.id_people_manager ?? pmId;
      const row = mapHierarchyUserRow(
        user,
        String(pmIdFromUser) === String(rootPmId) ? rootPmName : (user.people_manager?.username || rootPmName),
      );
      if (!usersById.has(row.id)) {
        usersById.set(row.id, row);
      }
      if (user.is_people_manager) {
        pmQueue.push(user.id);
      }
    }
  }

  return Array.from(usersById.values());
}

/**
 * Gerarchia PM sotto rootPmId.
 * Usa BFS sui report diretti: tollera cicli in id_people_manager e non dipende
 * dall'endpoint batch (hierarchy_root_id), che va in timeout se la gerarchia ha loop.
 */
export async function fetchManagedUsersHierarchyFromApi(rootPmId, rootPmName = 'Direct reports') {
  return fetchManagedUsersHierarchyBfs(rootPmId, rootPmName);
}

export function buildAbsencesByUserIdMap(entries) {
  const byUser = {};
  (entries || []).forEach((v) => {
    const day = parseInt(v.date.slice(8, 10), 10);
    if (!byUser[String(v.user_id)]) byUser[String(v.user_id)] = new Map();
    byUser[String(v.user_id)].set(day, v);
  });
  return byUser;
}

export function getUserDisplayName(user) {
  if (!user) return '';
  if (typeof user === 'string') return user;
  return user.name || user.username || user.email || '';
}

export function compareUsersByDisplayName(a, b) {
  return getUserDisplayName(a).localeCompare(getUserDisplayName(b), undefined, { sensitivity: 'base' });
}

export function sortUsersByDisplayName(users) {
  return [...(users || [])].sort(compareUsersByDisplayName);
}

export function sortNamesAlphabetically(names) {
  return [...(names || [])].sort((a, b) =>
    String(a).localeCompare(String(b), undefined, { sensitivity: 'base' })
  );
}

const DIRECT_REPORTS_GROUP = 'Direct reports';

export function getPeopleManagerGroupName(user) {
  return user?.peopleManagerName || DIRECT_REPORTS_GROUP;
}

function comparePeopleManagerGroups(a, b) {
  if (a === DIRECT_REPORTS_GROUP) return -1;
  if (b === DIRECT_REPORTS_GROUP) return 1;
  return a.localeCompare(b, undefined, { sensitivity: 'base' });
}

/** Alphabetical within each People Manager group; groups sorted by PM name (direct reports first). */
export function sortUsersGroupedByPeopleManager(users) {
  const groups = new Map();
  (users || []).forEach((user) => {
    const key = getPeopleManagerGroupName(user);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(user);
  });

  const result = [];
  [...groups.keys()]
    .sort(comparePeopleManagerGroups)
    .forEach((key) => {
      result.push(...sortUsersByDisplayName(groups.get(key)));
    });
  return result;
}
